import React, { useState } from 'react';
import { Shield, Download, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { Badge } from '@/app/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/app/components/ui/table';
import { exportAuditLogsCsv, useAuditLogs, useOrgTimeZone } from '@/hooks/use-settings';
import { Skeleton } from '@/app/components/ui/skeleton';
import { usePermissions } from '@/hooks/use-permissions';
import {
  endOfDayInTimeZone,
  formatDateTimeInTimeZone,
  isValidDateOnly,
  startOfDayInTimeZone,
  todayInTimeZone,
} from '@/lib/date-utils';
import type { AuditAction } from '@/lib/types';

export function AuditLogsPage() {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const { canViewAuditLogs } = usePermissions();
  const orgTimeZone = useOrgTimeZone();
  const [filterAction, setFilterAction] = useState<string>('all');
  const [filterModel, setFilterModel] = useState<string>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const validStartDate = isValidDateOnly(startDate) ? startDate : undefined;
  const validEndDate = isValidDateOnly(endDate) ? endDate : undefined;

  const filters = {
    action: filterAction !== 'all' ? filterAction : undefined,
    model_name: filterModel !== 'all' ? filterModel : undefined,
    start_date: validStartDate
      ? startOfDayInTimeZone(validStartDate, orgTimeZone)
      : undefined,
    end_date: validEndDate ? endOfDayInTimeZone(validEndDate, orgTimeZone) : undefined,
  };
  const orgToday = todayInTimeZone(orgTimeZone);

  const { data: logsData, isLoading, error } = useAuditLogs(filters);
  const { data: todayLogsData } = useAuditLogs({
    action: filters.action,
    model_name: filters.model_name,
    start_date: startOfDayInTimeZone(orgToday, orgTimeZone),
    end_date: endOfDayInTimeZone(orgToday, orgTimeZone),
  });

  if (!canViewAuditLogs) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Access Denied</h3>
                <p className="text-sm text-red-800 mt-1">
                  You do not have permission to view audit logs. Only administrators can access this page.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const logs = logsData?.results || [];

  const getActionBadge = (action: AuditAction) => {
    switch (action) {
      case 'create':
        return <Badge className="bg-green-600">Create</Badge>;
      case 'update':
        return <Badge className="bg-blue-600">Update</Badge>;
      case 'delete':
        return <Badge variant="destructive">Delete</Badge>;
      case 'complete':
        return <Badge className="bg-green-700">Complete</Badge>;
      case 'skip':
        return <Badge className="bg-amber-600">Skip</Badge>;
      default:
        return <Badge variant="secondary">{action}</Badge>;
    }
  };

  const handleExport = async () => {
    setExportError(null);
    setExporting(true);
    try {
      const blob = await exportAuditLogsCsv(filters);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `audit-logs-${todayInTimeZone(orgTimeZone)}.csv`;
      document.body.appendChild(anchor);
      try {
        anchor.click();
      } finally {
        anchor.remove();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      setExportError(
        err instanceof Error && err.message
          ? err.message
          : 'Audit log export failed. Please try again.'
      );
    } finally {
      setExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-1/4" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Error Loading Audit Logs</h3>
                <p className="text-sm text-red-800 mt-1">
                  Failed to load audit logs. Please try refreshing the page.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-gray-600 mt-1">View system activity and change history</p>
        </div>
        <Button variant="outline" onClick={handleExport} disabled={exporting}>
          <Download className="h-4 w-4 mr-2" />
          Export Logs
        </Button>
      </div>

      {exportError && (
        <p role="alert" className="text-sm text-red-700">{exportError}</p>
      )}

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900">Read-Only Access</h3>
              <p className="text-sm text-blue-800 mt-1">
                Audit logs are automatically recorded and cannot be modified or deleted. 
                All system changes are tracked for compliance and security purposes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Events</CardTitle>
            <Shield className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{logsData?.count || 0}</div>
            <p className="text-xs text-gray-600 mt-1">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Events Today</CardTitle>
            <Shield className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{todayLogsData?.count || 0}</div>
            <p className="text-xs text-gray-600 mt-1">Calendar day in {orgTimeZone}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unique Users</CardTitle>
            <Shield className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(logs.map(log => log.user_username)).size}
            </div>
            <p className="text-xs text-gray-600 mt-1">On this page</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Table */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Log</CardTitle>
          <CardDescription>Complete history of system changes and user actions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 flex gap-2">
              <Input
                type="date"
                placeholder="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full lg:w-auto"
              />
              <Input
                type="date"
                placeholder="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full lg:w-auto"
              />
            </div>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="w-full lg:w-[180px]">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
                <SelectItem value="skip">Skip</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterModel} onValueChange={setFilterModel}>
              <SelectTrigger className="w-full lg:w-[180px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Asset">Asset</SelectItem>
                <SelectItem value="Inspection">Inspection</SelectItem>
                <SelectItem value="Deployment">Deployment</SelectItem>
                <SelectItem value="RopeCut">RopeCut</SelectItem>
                <SelectItem value="MaintenanceRecord">Maintenance</SelectItem>
                <SelectItem value="Certification">Certification</SelectItem>
                <SelectItem value="JSA">JSA</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time ({orgTimeZone})</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Object</TableHead>
                  <TableHead>Changes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                      No audit logs found matching your filters
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-xs">
                        {formatDateTimeInTimeZone(log.timestamp, orgTimeZone, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </TableCell>
                      <TableCell className="font-medium">{log.user_username || 'System'}</TableCell>
                      <TableCell>{getActionBadge(log.action)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{log.model_name}</Badge>
                      </TableCell>
                      <TableCell>{log.object_repr}</TableCell>
                      <TableCell className="max-w-xs">
                        <span className="text-sm text-gray-600 truncate block">
                          {typeof log.changes === 'object' ? JSON.stringify(log.changes) : log.changes}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {logs.length > 0 && (
            <p className="text-sm text-gray-600">
              Showing {logs.length} of {logsData?.count || 0} total events
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
